import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import Highcharts from "highcharts";
import highchartsMore from "highcharts/highcharts-more";
import HighchartsReact from "highcharts-react-official";

if (typeof Highcharts === "object") {
    highchartsMore(Highcharts);
}

interface IBoxPlotChartProps {
    min: number;
    max: number;
    average: number;
}

interface RangePoint extends Highcharts.Point {
    low?: number;
    high?: number;
}

const localeForNumber = (lang: string) => (lang === "pl" ? "pl-PL" : "en-GB");

export const BoxPlotChart = ({min, max, average}: IBoxPlotChartProps) => {
    const {t, i18n} = useTranslation();
    const numberLocale = localeForNumber(i18n.language);

    const options: Highcharts.Options = useMemo(
        () => ({
            chart: {
                type: "columnrange",
                height: 200
            },
            exporting: {enabled: false},
            title: {
                text: undefined
            },
            legend: {
                enabled: false
            },
            xAxis: {
                categories: [t("offer_box.chart_price_per_m2")],
                title: {
                    text: null
                },
                labels: {
                    style: {
                        fontSize: "14px",
                        fontWeight: "600"
                    }
                }
            },
            yAxis: {
                title: {
                    text: t("offer_box.chart_price_pln")
                },
                labels: {
                    formatter: function () {
                        return this.value.toLocaleString(numberLocale);
                    }
                },
                plotLines: [
                    {
                        value: average,
                        color: "#4caf50",
                        width: 3,
                        zIndex: 4,
                        dashStyle: "Solid",
                        label: {
                            text: `${t("offer_box.chart_average_label")}: ${average.toLocaleString(numberLocale)} zł`,
                            align: "right",
                            style: {
                                color: "#4caf50",
                                fontWeight: "bold",
                                fontSize: "12px"
                            }
                        }
                    }
                ]
            },
            tooltip: {
                headerFormat: "<em>{point.key}</em><br/>",
                pointFormatter: function (this: RangePoint) {
                    return `
                        <b>${t("offer_box.chart_tooltip_maximum")}:</b> ${this.high?.toLocaleString(numberLocale)} zł<br/>
                        <b>${t("offer_box.chart_tooltip_minimum")}:</b> ${this.low?.toLocaleString(numberLocale)} zł<br/>
                        <b>${t("offer_box.chart_tooltip_range")}:</b> ${((this.high || 0) - (this.low || 0)).toLocaleString(numberLocale)} zł<br/>
                        <br/>
                        <span style="color: #4caf50"><b>${t("offer_box.chart_average_label")}:</b> ${average.toLocaleString(numberLocale)} zł</span>
                    `;
                },
                style: {
                    fontSize: "13px"
                }
            },
            plotOptions: {
                columnrange: {
                    borderRadius: 4,
                    borderWidth: 2,
                    borderColor: "#666",
                    color: "#e0e0e0",
                    // Make the middle "bar" slimmer
                    pointWidth: 40,
                    dataLabels: {
                        enabled: false
                    }
                }
            },
            series: [
                {
                    name: t("offer_box.chart_series_name"),
                    type: "columnrange",
                    data: [
                        {
                            low: min,
                            high: max
                        }
                    ]
                }
            ],
            credits: {
                enabled: false
            }
        }),
        [min, max, average, t, i18n.language]
    );

    return <HighchartsReact highcharts={Highcharts} options={options} />;
};
